import { JobPositionLabel } from "@/base";
import { Document } from "@/components";
import { useApiServiceContext } from "@/contexts";
import { Card, CardBody, cn } from "@heroui/react";
import type {
    ApplicantDocument,
    ApplicantSharedWithRelations,
} from "@sr-partner-portal/common";
import { IconMail, IconPhone } from "@tabler/icons-react";
import { useCallback } from "react";
import { useParams } from "react-router";
import { ApplicantExternalComments } from "./applicant-external-comments";
import { ApplicantExternalNotes } from "./applicant-external-notes";
import { ApplicantInternalNotes } from "./applicant-internal-notes";

const SharedApplicant = ({
    applicant,
    documents,
}: {
    documents: ApplicantDocument[];
    applicant: ApplicantSharedWithRelations;
}) => {
    const apiService = useApiServiceContext();
    const { token } = useParams();

    const handleDownload = useCallback(
        async ({ id }: { id: number }) => {
            const url =
                await apiService.applicantDocuments.getDownloadUrlByShareLinkToken(
                    {
                        id,
                        token,
                    },
                );
            window.open(url.downloadUrl, "_blank");
        },
        [apiService.applicantDocuments.getDownloadUrlByShareLinkToken, token],
    );

    const getPreviewUrl = useCallback(
        async ({ id }: { id: number }) => {
            const response =
                await apiService.applicantDocuments.getPreviewUrlByShareLinkToken(
                    {
                        id,
                        token,
                    },
                );
            return response.previewUrl;
        },
        [apiService.applicantDocuments.getPreviewUrlByShareLinkToken, token],
    );

    const companyName =
        applicant.jobPosition.companyShortName ||
        applicant.jobPosition.companyLongName;

    return (
        <div className="flex h-full w-full max-w-4xl flex-col gap-4 text-center">
            {applicant && (
                <Card
                    shadow="none"
                    classNames={{
                        base: "border border-default-200",
                    }}
                >
                    <CardBody>
                        <div className="flex w-full flex-col items-start">
                            <div className="font-bold text-xl">
                                {applicant.name}
                            </div>
                            <JobPositionLabel
                                jobPosition={applicant.jobPosition}
                                muted={true}
                            />
                            <div className="mt-2 flex items-center gap-2">
                                <IconMail
                                    className="inline shrink-0 text-default-500"
                                    stroke={1.5}
                                    size={20}
                                />
                                {applicant.emailAddress || "-"}
                            </div>
                            <div className="flex items-center gap-2">
                                <IconPhone
                                    className="inline shrink-0 text-default-500"
                                    stroke={1.5}
                                    size={20}
                                />
                                {applicant.phoneNumber || "-"}
                            </div>
                        </div>
                    </CardBody>
                </Card>
            )}
            <Card
                shadow="none"
                classNames={{
                    base: "border border-default-200",
                }}
            >
                <CardBody>
                    <div className="flex flex-col gap-2">
                        <div>Dokumente</div>
                        {documents?.length === 0 && (
                            <div className="flex w-full items-center justify-center text-default-500">
                                Keine Dokumente vorhanden
                            </div>
                        )}
                        <div className="grid grid-cols-[repeat(auto-fill,minmax(150px,1fr))] gap-4">
                            {documents?.map((item, index) => (
                                <Document
                                    key={item.id || `${item.name}-${index}`}
                                    document={item}
                                    isLoading={!item.id}
                                    onDownload={handleDownload}
                                    shareLinkToken={token}
                                    onGetPreviewUrl={getPreviewUrl}
                                    hideUnreadTag={true}
                                />
                            ))}
                        </div>
                    </div>
                </CardBody>
            </Card>
            <div
                className={cn(
                    "grid w-full grid-cols-1 gap-4 md:grid-cols-2",
                    applicant?.profile ? "" : "md:grid-cols-1",
                )}
            >
                <ApplicantInternalNotes
                    applicant={applicant}
                    isViewOnly={true}
                />
                <Card
                    shadow="none"
                    classNames={{
                        base: "border border-default-200",
                    }}
                >
                    <CardBody>
                        {applicant && (
                            <ApplicantExternalNotes
                                applicant={applicant}
                                companyName={companyName}
                                shareLinkToken={token}
                                isViewOnly={true}
                            />
                        )}
                    </CardBody>
                </Card>
            </div>
            <Card
                shadow="none"
                classNames={{
                    base: "border border-default-200",
                }}
            >
                <CardBody>
                    <div className="flex flex-col gap-2">
                        <div>
                            {companyName
                                ? `${companyName} Kommentar`
                                : "Kunden Kommentar"}
                        </div>
                        <ApplicantExternalComments
                            applicant={applicant}
                            shareLinkToken={token}
                        />
                    </div>
                </CardBody>
            </Card>
        </div>
    );
};

export { SharedApplicant };
